import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useQuery, QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import { withTheme } from 'styled-components';
import Script from 'next/script';

// import { getHeaderByUrl, getFooter } from 'api';

import { removeQueryString } from 'shared/helpers';

import { Header, Footer } from 'shared/components';
import axios from 'axios';

async function fetchFooter() {
	return axios
		.get('/api/global/getfooter')
		.then((res) => res.data)
		.catch((e) => {
			console.error('Error fetching footer', e);
		});
}

async function fetchHeader(url) {
	return axios
		.get('/api/global/getnavigation?path=' + url)
		.then((res) => res.data)
		.catch((e) => {
			console.error('Error fetching header', e);
		});
}

export const Layout = ({
	children,
	MetaDescription = '',
	MetaTitle = '',
	MetaImage = '',
	noHeader = true,
	slug = '',
}) => {
	const { asPath, push } = useRouter();
	const urlToGet = removeQueryString(asPath);
	const { data: headerData } = useQuery(['header', urlToGet], () =>
		fetchHeader(urlToGet),
	);

	useEffect(() => {
		window.addEventListener(
			'CookiebotOnDialogDisplay',
			() => (document.body.style.overflow = 'hidden'),
			false,
		);
		window.addEventListener(
			'CookiebotOnTagsExecuted',
			() => (document.body.style.overflow = 'unset'),
			false,
		);
		window.addEventListener(
			'CookiebotOnConsentReady',
			() => push(asPath, null, { shallow: true }),
			false,
		);
		() => {
			window.removeEventListener('CookiebotOnDialogInit');
			window.removeEventListener('CookiebotOnTagsExecuted');
			window.removeEventListener('CookiebotOnConsentReady');
		};
	}, []);
	const { data: footerData } = useQuery('footer', () => fetchFooter());

	return (
		<>
			<Script
				id="Cookiebot"
				src="https://consent.cookiebot.com/uc.js"
				data-cbid="8b4ee50f-210c-4217-a85d-ddfd30cd0a9a"
				strategy="beforeInteractive"
				data-blockingmode="manual"
				type="text/javascript"
			/>
			<Script
				id="matomo-script"
				dangerouslySetInnerHTML={{
					__html: `let _mtm = window._mtm = window._mtm || [];
			_mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});
			var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
			g.async=true; g.src='https://cdn.matomo.cloud/tco.matomo.cloud/container_OhLptOt9.js'; s.parentNode.insertBefore(g,s);`,
				}}
			/>
			<Head>
				<title>{MetaTitle}</title>
				<meta
					name="description"
					content={
						MetaDescription ? MetaDescription.substring(0, 130) : ''
					}
				/>
				<link rel="icon" href="/favicon.ico" />
				<meta
					property="og:url"
					content={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${slug}`}
					key="ogurl"
				/>
				<meta property="og:image" content={MetaImage} key="ogimage" />
				<meta property="og:site_name" content="TCO" key="ogsitename" />
				<meta property="og:title" content={MetaTitle} key="ogtitle" />
				<meta
					property="og:description"
					content={MetaDescription}
					key="ogdesc"
				/>
				{/* Note: can not be renaimed to X. X still use twitter in its syntax for this.  */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					property="twitter:image"
					content={MetaImage}
					key="twitterimage"
				/>
				<meta
					property="twitter:site"
					content="TCO"
					key="twittersitename"
				/>
				<meta
					property="twitter:description"
					content={MetaDescription}
					key="twitterdesc"
				/>
				<meta
					property="twitter:title"
					content={MetaTitle}
					key="twittertitle"
				/>
			</Head>
			{headerData && !noHeader && <Header {...headerData} />}
			<main>{children}</main>
			<Footer {...footerData} />
		</>
	);
};

export async function getStaticProps({ params }) {
	const queryClient = new QueryClient();
	let url = `/${params.url.join('/')}`;

	await queryClient.prefetchQuery(['header', url], () => fetchHeader(url));
	await queryClient.prefetchQuery('footer', () => fetchFooter());
	return {
		props: {
			dehydratedState: dehydrate(queryClient),
		},
		revalidate: 10,
	};
}

export const LayoutContainer = withTheme(Layout);

LayoutContainer.propTypes = {
	children: PropTypes.node,
};
